import React, { useEffect, useMemo, useRef, useState } from 'react';
import { 
  Box, Button, Divider, FormControlLabel, Switch, TextField, Typography, Paper, MenuItem, 
  Tooltip, IconButton, Accordion, AccordionSummary, AccordionDetails,
  CircularProgress, Snackbar, Alert, Tabs, Tab, Chip, Slider, useTheme, useMediaQuery
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import ImageIcon from '@mui/icons-material/Image';
import SettingsIcon from '@mui/icons-material/Settings';
import PhotoSizeSelectActualIcon from '@mui/icons-material/PhotoSizeSelectActual';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import VectorIcon from '@mui/icons-material/IntegrationInstructions';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import axiosInstance from '../../services/axiosInstance';
import { API_BASE_URL } from '../../config';

type SizingMode = 'original' | 'contain' | 'cover' | 'inside' | 'width' | 'height';

const SIZING_MODE_OPTIONS: Array<{ value: SizingMode; label: string }> = [
  { value: 'original', label: 'Original (behåll storlek)' },
  { value: 'contain', label: 'Contain (fyll ut box)' },
  { value: 'cover', label: 'Cover (beskär till box)' },
  { value: 'inside', label: 'Inside (max inom box)' },
  { value: 'width', label: 'Fast bredd' },
  { value: 'height', label: 'Fast höjd' },
];

const FIT_FOCUS_OPTIONS = ['center', 'attention', 'entropy'] as const;
const RESIZE_HANDLE_WIDTH = 14;
const MIN_LEFT_PANEL_WIDTH = 320;
const MIN_RIGHT_PANEL_WIDTH = 360;

const ACCORDION_SX = {
  backgroundColor: '#fafafa',
  mb: 1,
  border: '1px solid #e0e0e0',
  borderRadius: '10px',
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { margin: '0 0 8px 0' },
};

const CLICKABLE_PANEL_SX = {
  p: 1.5,
  border: '1px solid #e0e0e0',
  borderRadius: '10px',
  cursor: 'pointer',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
  '&:hover': {
    borderColor: '#90caf9',
    boxShadow: '0 4px 14px rgba(7,47,83,0.08)',
    transform: 'translateY(-1px)',
  },
};

function toPositiveInt(value: unknown): number | undefined {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.round(n);
}

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function normalizeSizingForm(sizing: any = {}) {
  const mode = (SIZING_MODE_OPTIONS.some(option => option.value === sizing?.mode) ? sizing.mode : 'inside') as SizingMode;
  let width = toPositiveInt(sizing?.width);
  let height = toPositiveInt(sizing?.height);
  let background = typeof sizing?.background === 'string' ? sizing.background : '';
  const fitFocus = FIT_FOCUS_OPTIONS.includes((sizing?.fitFocus || 'center') as any)
    ? sizing.fitFocus || 'center'
    : 'center';
  const zoom = clampNumber(sizing?.zoom, 1, 5, 1);
  const focusX = clampNumber(sizing?.focusX, 0, 100, 50);
  const focusY = clampNumber(sizing?.focusY, 0, 100, 50);

  if (mode === 'original') {
    width = undefined;
    height = undefined;
    background = '';
  }
  if (mode === 'width') {
    height = undefined;
    background = '';
  }
  if (mode === 'height') {
    width = undefined;
    background = '';
  }
  if (mode !== 'contain') {
    background = '';
  }

  return {
    mode,
    width: width ?? 0,
    height: height ?? 0,
    background,
    fitFocus,
    zoom,
    focusX,
    focusY,
  };
}

function normalizeTemplateForForm(template: any) {
  return {
    ...defaultTemplate,
    ...template,
    output: {
      ...defaultTemplate.output,
      ...(template?.output || {}),
    },
    sizing: normalizeSizingForm({
      ...defaultTemplate.sizing,
      ...(template?.sizing || {}),
    }),
    vector: {
      ...defaultTemplate.vector,
      ...(template?.vector || {}),
    },
    watermark: {
      ...defaultTemplate.watermark,
      ...(template?.watermark || {}),
      textOptions: {
        ...defaultTemplate.watermark.textOptions,
        ...(template?.watermark?.textOptions || {}),
      },
      imageOptions: {
        ...(template?.watermark?.imageOptions || {}),
      },
    },
    fileNaming: {
      ...defaultTemplate.fileNaming,
      ...(template?.fileNaming || {}),
    },
    cacheTTLSeconds: template?.cacheTTLSeconds ?? defaultTemplate.cacheTTLSeconds,
  };
}

function serializeTemplateForApi(template: any) {
  const normalized = normalizeTemplateForForm(template);
  return {
    ...normalized,
    output: {
      ...normalized.output,
      colorSpace: normalized.output.colorSpace || undefined,
    },
    sizing: {
      ...normalized.sizing,
      width: toPositiveInt(normalized.sizing.width),
      height: toPositiveInt(normalized.sizing.height),
      background: normalized.sizing.background || undefined,
      zoom: clampNumber(normalized.sizing.zoom, 1, 5, 1),
      focusX: clampNumber(normalized.sizing.focusX, 0, 100, 50),
      focusY: clampNumber(normalized.sizing.focusY, 0, 100, 50),
    },
  };
}

async function getRequestErrorMessage(err: any, fallback: string): Promise<string> {
  const responseData = err?.response?.data;
  if (responseData && typeof responseData.text === 'function') {
    try {
      const text = await responseData.text();
      if (!text) return fallback;
      try {
        const parsed = JSON.parse(text);
        return parsed?.message || text || fallback;
      } catch {
        return text || fallback;
      }
    } catch {}
  }
  return err?.response?.data?.message || err?.message || fallback;
}

function getSizingModeHelperText(mode: SizingMode): string {
  switch (mode) {
    case 'original':
      return 'Ingen resize. Behaller källans storlek.';
    case 'contain':
      return 'Passar bilden inom boxen. Kan fylla ut med vit bakgrund till exakt storlek.';
    case 'cover':
      return 'Fyller boxen och beskär bilden till exakt storlek.';
    case 'inside':
      return 'Skalar proportionerligt utan padding. Slutstorleken kan bli mindre än boxen.';
    case 'width':
      return 'Sätter fast bredd. Höjden räknas ut automatiskt.';
    case 'height':
      return 'Sätter fast höjd. Bredden räknas ut automatiskt.';
    default:
      return '';
  }
}

function describeSizing(sizing: any): string {
  const normalized = normalizeSizingForm(sizing);
  if (normalized.mode === 'original') return 'Original storlek';

  const sizeLabel = normalized.width && normalized.height
    ? `${normalized.width}x${normalized.height}`
    : normalized.width
      ? `${normalized.width}px bred`
      : normalized.height
        ? `${normalized.height}px hog`
        : 'auto';

  const parts = [normalized.mode, sizeLabel];
  if (normalized.mode === 'contain' && normalized.background === '#ffffff') {
    parts.push('vit bakgrund');
  }
  if (normalized.mode === 'cover') {
    parts.push(`fokus ${normalized.fitFocus}`);
    if (
      Math.abs(normalized.zoom - 1) > 0.001 ||
      Math.abs(normalized.focusX - 50) > 0.001 ||
      Math.abs(normalized.focusY - 50) > 0.001
    ) {
      parts.push(`zoom ${Math.round(normalized.zoom * 100)}%`);
      parts.push('manuellt utsnitt');
    }
  }
  return parts.join(' • ');
}

function matchesPreset(form: any, preset: any): boolean {
  const normalizedForm = normalizeTemplateForForm(form);
  const normalizedPreset = normalizeTemplateForForm({
    ...defaultTemplate,
    ...preset,
    output: { ...defaultTemplate.output, ...(preset.output || {}) },
    sizing: { ...defaultTemplate.sizing, ...(preset.sizing || {}) },
    vector: { ...defaultTemplate.vector, ...(preset.vector || {}) },
  });

  return (
    normalizedForm.output.format === normalizedPreset.output.format &&
    (normalizedForm.output.dpi || null) === (normalizedPreset.output.dpi || null) &&
    normalizedForm.sizing.mode === normalizedPreset.sizing.mode &&
    normalizedForm.sizing.width === normalizedPreset.sizing.width &&
    normalizedForm.sizing.height === normalizedPreset.sizing.height &&
    normalizedForm.sizing.background === normalizedPreset.sizing.background &&
    normalizedForm.sizing.zoom === normalizedPreset.sizing.zoom &&
    normalizedForm.sizing.focusX === normalizedPreset.sizing.focusX &&
    normalizedForm.sizing.focusY === normalizedPreset.sizing.focusY &&
    (normalizedForm.vector?.density || null) === (normalizedPreset.vector?.density || null)
  );
}

const defaultTemplate = {
  name: '',
  description: '',
  isActive: true,
  showInAssetMenus: false,
  order: 0,
  output: { format: 'jpg', quality: 85, stripMetadata: true, colorSpace: '' },
  sizing: { mode: 'inside' as SizingMode, width: 1920, height: 1920, background: '', fitFocus: 'center', zoom: 1, focusX: 50, focusY: 50 },
  vector: { density: 300, useClippingPath: false, transparentBG: true },
  watermark: { type: 'none' as const, textOptions: { color: '#ffffff', opacity: 1 } },
  fileNaming: { pattern: '{title}_{w}x{h}.{ext}', lowercase: true, safeChars: true },
  cacheTTLSeconds: 86400,
};

// Preset templates
const presets = {
  web: {
    name: 'Webb',
    description: 'Optimerad för webbanvändning',
    output: { format: 'jpg', quality: 85, stripMetadata: true, colorSpace: 'sRGB' },
    sizing: { mode: 'inside', width: 1920, height: 1920 },
    vector: { density: 150, useClippingPath: false, transparentBG: false },
  },
  productWeb: {
    name: 'Produkt 1800x1440',
    description: 'Produktbild med vit bakgrund för webb',
    output: { format: 'jpg', quality: 90, stripMetadata: true, colorSpace: 'sRGB' },
    sizing: { mode: 'contain', width: 1800, height: 1440, background: '#ffffff' },
    vector: { density: 150, useClippingPath: false, transparentBG: false },
  },
  wineWeb: {
    name: 'Vin 1200x1400',
    description: 'Vinbild med vit bakgrund för webb',
    output: { format: 'jpg', quality: 90, stripMetadata: true, colorSpace: 'sRGB' },
    sizing: { mode: 'contain', width: 1200, height: 1400, background: '#ffffff' },
    vector: { density: 150, useClippingPath: false, transparentBG: false },
  },
  socialMedia: {
    name: 'Instagram 1080x1080',
    description: 'Kvadratisk social post',
    output: { format: 'jpg', quality: 90, stripMetadata: true, colorSpace: 'sRGB', dpi: 72 },
    sizing: { mode: 'cover', width: 1080, height: 1080 },
    vector: { density: 72, useClippingPath: false, transparentBG: false },
  },
  instagramPortrait: {
    name: 'Instagram 1080x1350',
    description: 'Stående Instagram-inlägg',
    output: { format: 'jpg', quality: 90, stripMetadata: true, colorSpace: 'sRGB', dpi: 72 },
    sizing: { mode: 'cover', width: 1080, height: 1350 },
    vector: { density: 72, useClippingPath: false, transparentBG: false },
  },
  instagramStory: {
    name: 'Instagram 1080x1920',
    description: 'Story eller reel-cover i fullhöjd',
    output: { format: 'jpg', quality: 90, stripMetadata: true, colorSpace: 'sRGB', dpi: 72 },
    sizing: { mode: 'cover', width: 1080, height: 1920 },
    vector: { density: 72, useClippingPath: false, transparentBG: false },
  },
  print: {
    name: 'Tryck',
    description: 'Högkvalitet för print/tryck',
    output: { format: 'tiff', quality: 100, stripMetadata: false, colorSpace: 'CMYK' },
    sizing: { mode: 'inside', width: 3000, height: 3000 },
    vector: { density: 300, useClippingPath: false, transparentBG: false },
  },
  transparentPng: {
    name: 'Transparent PNG',
    description: 'PNG med transparent bakgrund',
    output: { format: 'png', quality: 95, stripMetadata: true, colorSpace: 'sRGB' },
    sizing: { mode: 'inside', width: 2000, height: 2000 },
    vector: { density: 300, useClippingPath: true, transparentBG: true },
  },
};

const DownloadTemplatesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const cropEditorRef = useRef<HTMLDivElement | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [form, setForm] = useState<any>(() => normalizeTemplateForForm(defaultTemplate));
  const [saving, setSaving] = useState(false);
  const [previewAssetId, setPreviewAssetId] = useState('');
  const [assetSearch, setAssetSearch] = useState('');
  const [assetOptions, setAssetOptions] = useState<any[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBytes, setPreviewBytes] = useState<number | null>(null);
  const [previewOutputInfo, setPreviewOutputInfo] = useState<{ width: number | null; height: number | null }>({ width: null, height: null });
  const [renderCommand, setRenderCommand] = useState<string>('');
  const [previewTab, setPreviewTab] = useState(0);
  const [wmImageData, setWmImageData] = useState<string>('');
  const [leftPaneWidth, setLeftPaneWidth] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [previewScale, setPreviewScale] = useState<number>(100);
  const [showOriginalCompare, setShowOriginalCompare] = useState(false);
  const [cropDragState, setCropDragState] = useState<null | { startX: number; startY: number; focusX: number; focusY: number }>(null);
  // Default watermark storage keys (browser-local)
  const DEFAULT_WM_KEY = 'downloadTemplates.defaultWatermarkImage';
  const DEFAULT_WM_META_KEY = 'downloadTemplates.defaultWatermarkMeta';
  const abortRef = useMemo(() => ({ ctrl: null as AbortController | null }), []);
  const [originalAssetInfo, setOriginalAssetInfo] = useState<any>(null);
  const [generating, setGenerating] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  
  // Toast notification state
  const [toast, setToast] = useState<{open: boolean; message: string; severity: 'success'|'error'|'info'}>({
    open: false, message: '', severity: 'success'
  });

  const fetchList = async () => {
    const { data } = await axiosInstance.get('/api/download-templates');
    setItems(data);
  };

  const updateSizing = (patch: Record<string, any>) => {
    setForm((prev: any) => normalizeTemplateForForm({
      ...prev,
      sizing: {
        ...(prev.sizing || {}),
        ...patch,
      },
    }));
  };

  const sizing = normalizeSizingForm(form.sizing);
  const sizingMode = sizing.mode;
  const containCanvasEnabled = sizingMode === 'contain' && !!sizing.width && !!sizing.height;
  const manualCoverEnabled = sizingMode === 'cover' && !!sizing.width && !!sizing.height;
  const currentTemplateTitle = form.name?.trim() || 'Ny nedladdningsmall';
  const selectedAssetLabel = assetSearch?.trim() || previewAssetId || '';
  const originalImageSrc = useMemo(() => {
    if (!originalAssetInfo) return '';
    const previewPath = originalAssetInfo.previewPaths?.medium || originalAssetInfo.previewPaths?.thumbnail;
    if (previewPath) return `${API_BASE_URL}${previewPath}`;
    if (originalAssetInfo.filePath) {
      return `${API_BASE_URL}/${String(originalAssetInfo.filePath).replace(/\\/g, '/')}`;
    }
    return '';
  }, [originalAssetInfo]);
  const previewScaleOptions = [100, 75, 50, 25];

  const triggerOnEnterOrSpace = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  const handleCreateNew = () => {
    setForm(normalizeTemplateForForm(defaultTemplate));
    setPreviewError(null);
    setToast({ open: true, message: 'Ny mall redo att fyllas i', severity: 'info' });
  };

  const adjustPaneWidth = (delta: number) => {
    const layout = layoutRef.current;
    if (!layout || isMobile) return;

    const maxLeft = Math.max(MIN_LEFT_PANEL_WIDTH, layout.clientWidth - MIN_RIGHT_PANEL_WIDTH - RESIZE_HANDLE_WIDTH);
    const baseWidth = leftPaneWidth ?? Math.round(layout.clientWidth * 0.4);
    const nextWidth = Math.min(maxLeft, Math.max(MIN_LEFT_PANEL_WIDTH, baseWidth + delta));
    setLeftPaneWidth(nextWidth);
  };

  const handleCropDragStart = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!manualCoverEnabled || !originalImageSrc) return;
    event.preventDefault();
    setCropDragState({
      startX: event.clientX,
      startY: event.clientY,
      focusX: sizing.focusX,
      focusY: sizing.focusY,
    });
  };

  useEffect(() => {
    fetchList().catch(() => {});
    // Load default watermark (if any) on mount and apply if watermark type is image
    try {
      const saved = localStorage.getItem(DEFAULT_WM_KEY);
      const meta = localStorage.getItem(DEFAULT_WM_META_KEY);
      if (saved) setWmImageData(saved);
      if (saved && meta && (form.watermark?.type === 'image')) {
        const parsed = JSON.parse(meta);
        setForm((prev:any) => ({
          ...prev,
          watermark: {
            ...prev.watermark,
            imageOptions: {
              ...(prev.watermark?.imageOptions||{}),
              data: saved,
              scale: parsed.scale ?? (prev.watermark?.imageOptions?.scale ?? 0.3),
              position: parsed.position ?? (prev.watermark?.imageOptions?.position || 'bottom-right'),
              opacity: parsed.opacity ?? (prev.watermark?.imageOptions?.opacity ?? 100)
            }
          }
        }));
      }
    } catch {}
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!isResizing || isMobile) return;

    const handleMouseMove = (event: MouseEvent) => {
      const layout = layoutRef.current;
      if (!layout) return;

      const rect = layout.getBoundingClientRect();
      const maxLeft = Math.max(MIN_LEFT_PANEL_WIDTH, rect.width - MIN_RIGHT_PANEL_WIDTH - RESIZE_HANDLE_WIDTH);
      const nextWidth = Math.min(
        maxLeft,
        Math.max(MIN_LEFT_PANEL_WIDTH, event.clientX - rect.left - RESIZE_HANDLE_WIDTH / 2)
      );

      setLeftPaneWidth(nextWidth);
    };

    const stopResizing = () => setIsResizing(false);

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopResizing);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isMobile, isResizing]);

  useEffect(() => {
    if (isMobile) setIsResizing(false);
  }, [isMobile]);

  useEffect(() => {
    if (!cropDragState || !manualCoverEnabled || !originalImageSrc) return;

    const handleMouseMove = (event: MouseEvent) => {
      const editor = cropEditorRef.current;
      if (!editor) return;
      const rect = editor.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const zoom = Math.max(1, Number(sizing.zoom || 1));
      const nextFocusX = clampNumber(
        cropDragState.focusX - ((event.clientX - cropDragState.startX) / rect.width) * (100 / zoom),
        0,
        100,
        50
      );
      const nextFocusY = clampNumber(
        cropDragState.focusY - ((event.clientY - cropDragState.startY) / rect.height) * (100 / zoom),
        0,
        100,
        50
      );

      updateSizing({
        focusX: Math.round(nextFocusX * 10) / 10,
        focusY: Math.round(nextFocusY * 10) / 10,
      });
    };

    const stopDragging = () => setCropDragState(null);

    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopDragging);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopDragging);
    };
  }, [cropDragState, manualCoverEnabled, originalImageSrc, sizing.zoom]);

  const applyPreset = (presetKey: keyof typeof presets) => {
    const preset = presets[presetKey];
    setForm((prev: any) => normalizeTemplateForForm({
      ...prev,
      name: preset.name, // alltid ersätt namnet med presetens namn
      description: preset.description,
      output: { ...prev.output, ...preset.output },
      sizing: { ...defaultTemplate.sizing, ...preset.sizing },
      vector: { ...prev.vector, ...preset.vector },
    }));
    setToast({ open: true, message: `Förinställning "${preset.name}" tillagd`, severity: 'info' });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const nextFormState = normalizeTemplateForForm(form);
      const payload = serializeTemplateForApi(form);
      setForm(nextFormState);
      const id = (form as any)._id || (form as any).id;
      if (id) {
        await axiosInstance.patch(`/api/download-templates/${id}`, payload);
        setToast({ open: true, message: 'Mall uppdaterad!', severity: 'success' });
      } else {
        const toCreate = { ...payload } as any;
        delete toCreate._id;
        delete toCreate.id;
        await axiosInstance.post('/api/download-templates', toCreate);
        setToast({ open: true, message: 'Mall skapad!', severity: 'success' });
      }
      await fetchList();
    } catch (err: any) {
      setToast({ open: true, message: err?.response?.data?.message || 'Kunde inte spara mall', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleDuplicate = (template: any) => {
    const duplicated = { 
      ...template, 
      name: `${template.name} (kopia)`,
      _id: undefined,
      id: undefined
    };
    setForm(normalizeTemplateForForm(duplicated));
    setToast({ open: true, message: `"${template.name}" duplicerad`, severity: 'info' });
  };

  const handleDelete = async (template: any) => {
    try {
      await axiosInstance.delete(`/api/download-templates/${template._id || template.id}`);
      await fetchList();
      setToast({ open: true, message: 'Mall borttagen', severity: 'success' });
    } catch {
      setToast({ open: true, message: 'Kunde inte ta bort mall', severity: 'error' });
    }
  };

  const generatePreview = async () => {
    if (!previewAssetId) return;
    setGenerating(true);
    setPreviewError(null);
    try { abortRef.ctrl?.abort(); } catch {}
    abortRef.ctrl = new AbortController();
    const currentCtrl = abortRef.ctrl;
    
    // Fetch original asset info first
    try {
      const assetResp = await axiosInstance.get(`/api/assets/${previewAssetId}`);
      if (abortRef.ctrl === currentCtrl) {
        setOriginalAssetInfo(assetResp.data);
      }
    } catch {
      if (abortRef.ctrl === currentCtrl) {
        setOriginalAssetInfo(null);
      }
    }
    
    try {
      const previewTemplate = serializeTemplateForApi(form);
      const resp = await axiosInstance.post('/api/download-templates/render/preview', { inlineTemplate: previewTemplate, assetId: previewAssetId }, { responseType: 'blob', signal: abortRef.ctrl.signal as any });
      if (abortRef.ctrl !== currentCtrl) return;
      const url = URL.createObjectURL(resp.data);
      setPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      try {
        const bytes = Number(resp.headers['x-output-bytes'] || resp.headers['X-Output-Bytes']);
        setPreviewBytes(!isNaN(bytes) ? bytes : null);
      } catch {
        setPreviewBytes(null);
      }
      try {
        const width = Number(resp.headers['x-output-width'] || resp.headers['X-Output-Width']);
        const height = Number(resp.headers['x-output-height'] || resp.headers['X-Output-Height']);
        setPreviewOutputInfo({
          width: !isNaN(width) && width > 0 ? width : null,
          height: !isNaN(height) && height > 0 ? height : null,
        });
      } catch {
        setPreviewOutputInfo({ width: null, height: null });
      }
      try {
        const cmd = decodeURIComponent(resp.headers['x-render-command'] || resp.headers['X-Render-Command'] || '');
        setRenderCommand(cmd);
      } catch {
        setRenderCommand('');
      }
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      if (abortRef.ctrl !== currentCtrl) return;
      setPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setPreviewBytes(null);
      setPreviewOutputInfo({ width: null, height: null });
      setRenderCommand('');
      setPreviewError(await getRequestErrorMessage(err, 'Kunde inte generera förhandsgranskning.'));
    } finally {
      if (abortRef.ctrl === currentCtrl) {
        setGenerating(false);
      }
    }
  };

  // Auto‑preview on changes (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      if (previewAssetId) generatePreview();
    }, 400);
    return () => clearTimeout(t);
  }, [JSON.stringify(form), previewAssetId]);

  const handleDownloadPreview = async () => {
    if (!previewAssetId) return;
    const resp = await axiosInstance.post('/api/download-templates/render/preview', { inlineTemplate: serializeTemplateForApi(form), assetId: previewAssetId }, { responseType: 'arraybuffer' });
    const disposition: string = resp.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const filename = match ? decodeURIComponent(match[1]) : 'preview';
    const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    setToast({ open: true, message: 'Förhandsgranskning nedladdad!', severity: 'success' });
  };

  const desktopGridTemplateColumns = leftPaneWidth === null
    ? `minmax(${MIN_LEFT_PANEL_WIDTH}px, 40%) ${RESIZE_HANDLE_WIDTH}px minmax(${MIN_RIGHT_PANEL_WIDTH}px, 1fr)`
    : `${leftPaneWidth}px ${RESIZE_HANDLE_WIDTH}px minmax(${MIN_RIGHT_PANEL_WIDTH}px, 1fr)`;

  return (
    <Box
      ref={layoutRef}
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : desktopGridTemplateColumns,
        gap: isMobile ? 2 : 0,
        height: '100%',
      }}
    >
      {/* LEFT PANEL - Template Settings */}
      <Paper sx={{ p: 2, overflow: 'auto', order: isMobile ? 2 : 1, minWidth: 0, mr: isMobile ? 0 : 1 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon color="primary" />
          Nedladdningsmallar
        </Typography>
        <Divider sx={{ my: 1 }} />

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: '12px',
            borderColor: '#dbe6f3',
            background: 'linear-gradient(180deg, #ffffff 0%, #f7faff 100%)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.04em' }}>
                Mallöversikt
              </Typography>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {currentTemplateTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bygg mallen i valfri ordning. Alla sektioner ar alltid tillgangliga, sa du kan hoppa tillbaka och justera nar som helst.
              </Typography>
            </Box>
            <Chip
              label={form.isActive ? 'Aktiv mall' : 'Utkast'}
              color={form.isActive ? 'success' : 'default'}
              size="small"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            <Chip size="small" color="primary" variant="outlined" label={form.output.format.toUpperCase()} />
            <Chip size="small" variant="outlined" label={describeSizing(form.sizing)} />
            {sizing.background === '#ffffff' && (
              <Chip size="small" variant="outlined" label="Vit bakgrund" />
            )}
            {form.watermark?.type !== 'none' && (
              <Chip size="small" variant="outlined" label={`Vattenstämpel: ${form.watermark.type}`} />
            )}
            {form.showInAssetMenus && (
              <Chip size="small" variant="outlined" label="Visas i resurs-menyer" />
            )}
          </Box>
        </Paper>

        <Accordion
          id="dt-section-presets"
          sx={ACCORDION_SX}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <BoltIcon sx={{ mr: 1, color: 'warning.main' }} />
            <Typography>Malltyper</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Välj en snabbmall som startpunkt och finjustera sedan fritt.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 1.25 }}>
              {Object.entries(presets).map(([presetKey, preset]) => {
                const active = matchesPreset(form, preset);
                return (
                  <Paper
                    key={presetKey}
                    variant="outlined"
                    onClick={() => applyPreset(presetKey as keyof typeof presets)}
                    onKeyDown={(event) => triggerOnEnterOrSpace(event, () => applyPreset(presetKey as keyof typeof presets))}
                    role="button"
                    tabIndex={0}
                    sx={{
                      ...CLICKABLE_PANEL_SX,
                      p: 1.5,
                      borderColor: active ? 'primary.main' : '#e0e0e0',
                      backgroundColor: active ? '#f4f8ff' : '#fff',
                    }}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      {preset.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {preset.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                      <Chip size="small" variant="outlined" label={String(preset.output.format).toUpperCase()} />
                      <Chip size="small" variant="outlined" label={describeSizing(preset.sizing)} />
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Accordion Sections */}
        <Accordion 
          id="dt-section-info"
          defaultExpanded 
          sx={ACCORDION_SX}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography>Grundinformation</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField 
                label="Namn" 
                value={form.name} 
                onChange={e => setForm({ ...form, name: e.target.value })}
                required
                error={!form.name}
                helperText={!form.name && "Namn krävs"}
              />
              <TextField label="Beskrivning" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              <FormControlLabel 
                control={<Switch checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Aktiv
                    {form.isActive && <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />}
                  </Box>
                }
              />
              <FormControlLabel control={<Switch checked={form.showInAssetMenus} onChange={e => setForm({ ...form, showInAssetMenus: e.target.checked })} />} label="Visa i resurs-menyer" />
              <Tooltip title="Sorteringsordning i menyer (lägre nummer = högre upp)">
                <TextField label="Ordning" type="number" value={form.order} onChange={e => setForm({ ...form, order: Number(e.target.value || 0) })} />
              </Tooltip>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          id="dt-section-output"
          defaultExpanded
          sx={ACCORDION_SX}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <ImageIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography>Utdata & Format</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 2 }}>
              <TextField select label="Format" value={form.output.format} onChange={e => setForm({ ...form, output: { ...form.output, format: e.target.value } })}>
                {['jpg','png','webp','tiff','pdf'].map(fmt => <MenuItem key={fmt} value={fmt}>{fmt.toUpperCase()}</MenuItem>)}
              </TextField>
              <Tooltip title="Kvalitet 1-100. Högre = bättre kvalitet men större filstorlek">
                <TextField label="Kvalitet" type="number" inputProps={{ min: 1, max: 100 }} value={form.output.quality} onChange={e => setForm({ ...form, output: { ...form.output, quality: Number(e.target.value) } })} />
              </Tooltip>
              <TextField select label="Färgrymd" value={form.output.colorSpace || ''} onChange={e => setForm({ ...form, output: { ...form.output, colorSpace: e.target.value } })}>
                <MenuItem value="">— oförändrad —</MenuItem>
                <MenuItem value="sRGB">sRGB (Webb)</MenuItem>
                <MenuItem value="CMYK">CMYK (Tryck)</MenuItem>
                <MenuItem value="GRAY">Gråskala</MenuItem>
              </TextField>
              <FormControlLabel 
                sx={{ gridColumn: '1 / -1' }}
                control={<Switch checked={!!form.output.stripMetadata} onChange={e => setForm({ ...form, output: { ...form.output, stripMetadata: e.target.checked } })} />} 
                label={
                  <Tooltip title="Ta bort EXIF/IPTC metadata för mindre filstorlek">
                    <span>Ta bort metadata</span>
                  </Tooltip>
                }
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          id="dt-section-sizing"
          defaultExpanded
          sx={ACCORDION_SX}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PhotoSizeSelectActualIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography>Storlek, Bakgrund & Beskärning</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 2 }}>
              <TextField 
                select 
                label="Läge" 
                value={sizingMode} 
                onChange={e => updateSizing({ mode: e.target.value })}
                helperText={getSizingModeHelperText(sizingMode)}
              >
                {SIZING_MODE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField 
                label="Bredd (px)" 
                type="number" 
                value={sizing.width} 
                onChange={e => updateSizing({ width: Number(e.target.value || 0) })} 
                disabled={sizingMode === 'original' || sizingMode === 'height'}
                helperText={sizingMode === 'original' ? 'Auto (fran källa)' : sizingMode === 'height' ? 'Auto (fran höjd)' : ''}
              />
              <TextField 
                label="Höjd (px)" 
                type="number" 
                value={sizing.height} 
                onChange={e => updateSizing({ height: Number(e.target.value || 0) })}
                disabled={sizingMode === 'original' || sizingMode === 'width'}
                helperText={sizingMode === 'original' ? 'Auto (fran källa)' : sizingMode === 'width' ? 'Auto (fran bredd)' : ''}
              />
              {sizingMode === 'contain' && (
                <TextField
                  select
                  label="Bakgrund vid utfyllnad"
                  value={sizing.background === '#ffffff' ? 'white' : 'none'}
                  onChange={e => updateSizing({ background: e.target.value === 'white' ? '#ffffff' : '' })}
                  disabled={!containCanvasEnabled}
                  helperText={containCanvasEnabled ? 'Vit bakgrund fyller ut till exakt box och ersätter transparenta pixlar med vitt.' : 'Contain med bakgrund kräver både bredd och höjd.'}
                >
                  <MenuItem value="none">Ingen utfyllnad</MenuItem>
                  <MenuItem value="white">Vit bakgrund</MenuItem>
                </TextField>
              )}
              {sizingMode === 'cover' && (
                <TextField
                  select
                  label="Fokus för beskärning"
                  value={sizing.fitFocus}
                  onChange={e => updateSizing({ fitFocus: e.target.value })}
                  helperText="Startläge för cover. Finjustera sedan utsnittet i previewpanelen."
                >
                  <MenuItem value="center">Center</MenuItem>
                  <MenuItem value="attention">Attention</MenuItem>
                  <MenuItem value="entropy">Entropy</MenuItem>
                </TextField>
              )}
              <Typography variant="caption" sx={{ gridColumn: '1 / -1', color: '#666' }}>
                `contain` fyller ut till exakt box. `inside` skalar utan padding. `cover` beskär till exakt box.
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Typography
          variant="overline"
          sx={{ display: 'block', color: 'primary.main', fontWeight: 700, letterSpacing: '0.04em', mt: 2, mb: 0.25 }}
        >
          Avancerade inställningar
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: '#666', mb: 1 }}>
          Använd dessa när mallen behöver vattenstämpel, vektorhantering eller specialanpassade filnamn.
        </Typography>

        <Accordion
          sx={ACCORDION_SX}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <WaterDropIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography>Vattenstämpel</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
              <TextField select label="Typ" value={form.watermark.type} onChange={e => setForm({ ...form, watermark: { ...form.watermark, type: e.target.value } })}>
                {['none','text','image'].map(w => <MenuItem key={w} value={w}>{w === 'none' ? 'Ingen' : w === 'text' ? 'Text' : 'Bild'}</MenuItem>)}
              </TextField>
              {form.watermark.type === 'text' ? (
                <>
                  <TextField label="Text" value={form.watermark.textOptions?.text || ''} onChange={e => setForm({ ...form, watermark: { ...form.watermark, textOptions: { ...(form.watermark.textOptions||{}), text: e.target.value } } })} />
                  <TextField label="Färg (hex)" value={form.watermark.textOptions?.color || '#ffffff'} onChange={e => setForm({ ...form, watermark: { ...form.watermark, textOptions: { ...(form.watermark.textOptions||{}), color: e.target.value } } })} />
                  <TextField label="Storlek (px)" type="number" value={form.watermark.textOptions?.size || 48} onChange={e => setForm({ ...form, watermark: { ...form.watermark, textOptions: { ...(form.watermark.textOptions||{}), size: Number(e.target.value||48) } } })} />
                  <TextField select label="Typsnitt" value={form.watermark.textOptions?.font || 'Arial'} onChange={e => setForm({ ...form, watermark: { ...form.watermark, textOptions: { ...(form.watermark.textOptions||{}), font: e.target.value } } })}>
                    <MenuItem value="Arial">Arial</MenuItem>
                    <MenuItem value="Helvetica">Helvetica</MenuItem>
                    <MenuItem value="sans-serif">Sans-serif (Kapra-liknande)</MenuItem>
                    <MenuItem value="Impact">Impact</MenuItem>
                    <MenuItem value="Times">Times</MenuItem>
                  </TextField>
                  <TextField select label="Position" value={form.watermark.textOptions?.position || 'bottom-right'} onChange={e => setForm({ ...form, watermark: { ...form.watermark, textOptions: { ...(form.watermark.textOptions||{}), position: e.target.value } } })}>
                    {['center','top-left','top-right','bottom-left','bottom-right'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </TextField>
                  <Tooltip title="0 = helt genomskinlig (osynlig), 100 = helt täckande">
                    <TextField 
                      label="Opacitet (%)" 
                      type="number" 
                      inputProps={{ min: 0, max: 100 }} 
                      value={Math.round((form.watermark.textOptions?.opacity ?? 1) * 100)} 
                      onChange={e => setForm({ ...form, watermark: { ...form.watermark, textOptions: { ...(form.watermark.textOptions||{}), opacity: Number(e.target.value) / 100 } } })} 
                    />
                  </Tooltip>
                  <Tooltip title="Rotera texten i grader (t.ex. 0, 30, 45)">
                    <TextField 
                      label="Rotation (°)" 
                      type="number" 
                      value={(form.watermark.textOptions as any)?.angle || 0} 
                      onChange={e => setForm({ ...form, watermark: { ...form.watermark, textOptions: { ...(form.watermark.textOptions||{}), angle: Number(e.target.value || 0) } } })} 
                    />
                  </Tooltip>
                </>
              ) : form.watermark.type === 'image' ? (
                <>
                  <Button variant="outlined" component="label">Ladda upp bild
                    <input hidden type="file" accept="image/*" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        const data = String(reader.result || '');
                        setWmImageData(data);
                        // Ensure sane defaults so watermark appears in preview
                        setForm({
                          ...form,
                          watermark: {
                            ...form.watermark,
                            imageOptions: {
                              ...(form.watermark.imageOptions||{}),
                              data,
                              scale: form.watermark.imageOptions?.scale ?? 0.3,
                              position: form.watermark.imageOptions?.position || 'bottom-right',
                              opacity: form.watermark.imageOptions?.opacity ?? 100
                            }
                          }
                        });
                      };
                      reader.readAsDataURL(file);
                    }} />
                  </Button>
                  <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
                    <Button size="small" onClick={() => {
                      if (!wmImageData) return;
                      try {
                        localStorage.setItem(DEFAULT_WM_KEY, wmImageData);
                        const meta = JSON.stringify({
                          scale: form.watermark.imageOptions?.scale ?? 0.3,
                          position: form.watermark.imageOptions?.position || 'bottom-right',
                          opacity: form.watermark.imageOptions?.opacity ?? 100
                        });
                        localStorage.setItem(DEFAULT_WM_META_KEY, meta);
                        setToast({ open: true, message: 'Vattenstämpel sparad som standard', severity: 'success' });
                      } catch {}
                    }}>Spara som standard</Button>
                    <Button size="small" onClick={() => {
                      try {
                        const saved = localStorage.getItem(DEFAULT_WM_KEY);
                        const metaRaw = localStorage.getItem(DEFAULT_WM_META_KEY);
                        if (saved) {
                          const meta = metaRaw ? JSON.parse(metaRaw) : {};
                          setWmImageData(saved);
                          setForm((prev:any) => ({
                            ...prev,
                            watermark: {
                              ...prev.watermark,
                              imageOptions: {
                                ...(prev.watermark.imageOptions||{}),
                                data: saved,
                                scale: meta.scale ?? (prev.watermark.imageOptions?.scale ?? 0.3),
                                position: meta.position ?? (prev.watermark.imageOptions?.position || 'bottom-right'),
                                opacity: meta.opacity ?? (prev.watermark.imageOptions?.opacity ?? 100)
                              }
                            }
                          }));
                        }
                      } catch {}
                    }}>Använd standard</Button>
                  </Box>
                  <TextField label="Skala (0-1)" type="number" inputProps={{ step: 0.1, min: 0, max: 1 }} value={form.watermark.imageOptions?.scale || 0.3} onChange={e => setForm({ ...form, watermark: { ...form.watermark, imageOptions: { ...(form.watermark.imageOptions||{}), scale: Number(e.target.value||0.3) } } })} />
                  <TextField select label="Position" value={form.watermark.imageOptions?.position || 'bottom-right'} onChange={e => setForm({ ...form, watermark: { ...form.watermark, imageOptions: { ...(form.watermark.imageOptions||{}), position: e.target.value } } })}>
                    {['center','top-left','top-right','bottom-left','bottom-right'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </TextField>
                  <Tooltip title="0 = helt genomskinlig (osynlig), 100 = helt täckande">
                    <TextField 
                      label="Opacitet (%)" 
                      type="number" 
                      inputProps={{ min: 0, max: 100 }} 
                      value={Math.round((form.watermark.imageOptions?.opacity ?? 1) * 100)} 
                      onChange={e => setForm({ ...form, watermark: { ...form.watermark, imageOptions: { ...(form.watermark.imageOptions||{}), opacity: Number(e.target.value) / 100 } } })} 
                    />
                  </Tooltip>
                </>
              ) : null}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          sx={ACCORDION_SX}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <VectorIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography>Vektor-alternativ</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 2 }}>
              <Tooltip title="DPI för vektorrasterisering (högre = bättre kvalitet)">
                <TextField label="Upplösning (DPI)" type="number" value={form.vector?.density||300} onChange={e => setForm({ ...form, vector: { ...(form.vector||{}), density: Number(e.target.value||300) } })} />
              </Tooltip>
              <FormControlLabel control={<Switch checked={!!form.vector?.useClippingPath} onChange={e => setForm({ ...form, vector: { ...(form.vector||{}), useClippingPath: e.target.checked } })} />} label="Använd urklippsbana" />
              <FormControlLabel control={<Switch checked={!!form.vector?.transparentBG} onChange={e => setForm({ ...form, vector: { ...(form.vector||{}), transparentBG: e.target.checked } })} />} label="Transparent bakgrund" />
              <TextField 
                label="ImageMagick extra argument (avancerat)" 
                value={form.vector?.customArgs||''} 
                onChange={e => setForm({ ...form, vector: { ...(form.vector||{}), customArgs: e.target.value } })} 
                helperText='Ex: -alpha set -channel alpha -clip' 
                fullWidth 
                sx={{ gridColumn: '1 / -1' }} 
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          sx={ACCORDION_SX}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TextFieldsIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography>Filnamn</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: 2 }}>
              <TextField label="Mönster" value={form.fileNaming.pattern} onChange={e => setForm({ ...form, fileNaming: { ...form.fileNaming, pattern: e.target.value } })} />
              <FormControlLabel control={<Switch checked={form.fileNaming.lowercase} onChange={e => setForm({ ...form, fileNaming: { ...form.fileNaming, lowercase: e.target.checked } })} />} label="gemener" />
              <FormControlLabel control={<Switch checked={form.fileNaming.safeChars} onChange={e => setForm({ ...form, fileNaming: { ...form.fileNaming, safeChars: e.target.checked } })} />} label="säkra tecken" />
            </Box>
            <Typography variant="caption" sx={{ display:'block', mt:1, color:'#666' }}>
              Tillgängliga platshållare: {'{title} {category} {w} {h} {ext} {date:YYYYMMDD} {template} {filename} {transparent}'}
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Box sx={{ display: 'grid', gap: 1, mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
            <Button 
              variant="contained" 
              onClick={handleSave} 
              disabled={saving || !form.name}
              fullWidth
              startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {saving ? 'Sparar...' : 'Spara mall'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCreateNew}
              fullWidth={isMobile}
            >
              Ny mall
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: '#666' }}>
            Ladda en snabbmall, justera fritt och spara när du är nöjd. Du kan alltid ladda in en sparad mall och fortsätta arbeta i den.
          </Typography>
        </Box>

        {/* Saved Templates List - Moved to bottom */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight="bold">Sparade mallar</Typography>
            <IconButton size="small" onClick={() => fetchList()}><RefreshIcon fontSize="small" /></IconButton>
          </Box>
          
          <Box sx={{ mb: 2, maxHeight: 200, overflow: 'auto', border: '1px solid #eee', borderRadius: 1, backgroundColor: '#fff' }}>
            {items.length === 0 ? (
              <Typography variant="caption" sx={{ p: 2, display: 'block', textAlign: 'center', color: '#999' }}>
                Inga mallar sparade ännu.
              </Typography>
            ) : items.map((t: any) => (
              <Box 
                key={t._id || t.id} 
                sx={{ 
                  p: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  borderBottom: '1px solid #f0f0f0',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { background: '#f9f9f9' }
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.name}
                    </Typography>
                    {t.isActive && <Chip label="Aktiv" size="small" color="success" sx={{ height: 18 }} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {t.output?.format?.toUpperCase()} • {describeSizing(t.sizing)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Ladda">
                    <IconButton size="small" onClick={() => setForm(normalizeTemplateForForm(t))}><ImageIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Duplicera">
                    <IconButton size="small" onClick={() => handleDuplicate(t)}><ContentCopyIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Ta bort">
                    <IconButton size="small" color="error" onClick={() => handleDelete(t)}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      {!isMobile && (
        <Box
          id="dt-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Ändra panelbredd"
          tabIndex={0}
          onMouseDown={() => setIsResizing(true)}
          onDoubleClick={() => setLeftPaneWidth(null)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              adjustPaneWidth(-40);
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              adjustPaneWidth(40);
            }
          }}
          sx={{
            order: 2,
            cursor: 'col-resize',
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'center',
            py: 1,
            position: 'relative',
            backgroundColor: isResizing ? 'action.hover' : 'transparent',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <Box
            sx={{
              width: '2px',
              borderRadius: '999px',
              backgroundColor: isResizing ? 'primary.main' : 'divider',
            }}
          />
        </Box>
      )}

      {/* RIGHT PANEL - Preview */}
      <Paper id="dt-section-preview" sx={{ p: 2, overflow: 'auto', order: isMobile ? 1 : 3, minWidth: 0, ml: isMobile ? 0 : 1 }}>
        <Typography variant="h6">Förhandsgranskning</Typography>
        <Divider sx={{ my: 1 }} />

        <Paper
          variant="outlined"
          sx={{
            p: 1.75,
            mb: 2,
            borderRadius: '12px',
            borderColor: '#dbe6f3',
            background: 'linear-gradient(180deg, #ffffff 0%, #f7faff 100%)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                Granska direkt medan du bygger
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Välj en testbild en gång. Därefter uppdateras previewn automatiskt när du ändrar mallens inställningar.
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gap: 1, minWidth: isMobile ? '100%' : 260 }}>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                {previewScaleOptions.map(scale => (
                  <Button
                    key={scale}
                    size="small"
                    variant={previewScale === scale ? 'contained' : 'outlined'}
                    onClick={() => setPreviewScale(scale)}
                  >
                    {scale}%
                  </Button>
                ))}
              </Box>
              <FormControlLabel
                sx={{ m: 0, justifyContent: isMobile ? 'flex-start' : 'flex-end' }}
                control={
                  <Switch
                    checked={showOriginalCompare}
                    onChange={e => setShowOriginalCompare(e.target.checked)}
                    disabled={!originalImageSrc}
                  />
                }
                label="Visa original bredvid"
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
            <Chip size="small" color="primary" variant="outlined" label={form.output.format.toUpperCase()} />
            <Chip size="small" variant="outlined" label={describeSizing(form.sizing)} />
            {selectedAssetLabel && (
              <Chip size="small" variant="outlined" label={`Testbild: ${selectedAssetLabel}`} />
            )}
          </Box>
        </Paper>
        
        {/* Asset Search */}
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: 1, alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
            <TextField 
              label="Sök bild (namn/artikelnummer)" 
              value={assetSearch} 
              onChange={async e => {
                const q = e.target.value; setAssetSearch(q);
                if (q.length < 2) { setAssetOptions([]); return; }
                try {
                  const operator = q.trim().includes(' ') ? 'AND' : 'OR';
                  // Filter to images only to avoid video (.mp4) entries that can't preview here
                  const { data } = await axiosInstance.get(`/api/assets`, { params: { search: q, operator, limit: 10, fileTypes: 'image' } });
                  setAssetOptions(data?.data || data || []);
                } catch { setAssetOptions([]); }
              }} 
              placeholder="börja skriva för förslag (mellanslag = AND)"
              size="small"
            />
            {assetOptions.length > 0 && (
              <Box sx={{ maxHeight: 180, overflow: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
                {assetOptions.map((a:any) => (
                  <Box key={a._id} sx={{ p: 1, cursor: 'pointer', '&:hover': { background:'#f0f7ff' }, display: 'flex', gap: 1, alignItems: 'center' }} onClick={() => { setPreviewAssetId(a._id); setAssetOptions([]); setAssetSearch(a.title || a.fileName || a._id); }}>
                    {a.previewPaths?.thumbnail && (
                      <img src={`${API_BASE_URL}${a.previewPaths.thumbnail}`} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">{a.title || a.fileName}</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>ID: {a._id}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            <TextField label="Test resurs-ID" value={previewAssetId} onChange={e => setPreviewAssetId(e.target.value)} placeholder="klistra in ett resurs-ID" size="small" />
          </Box>
          <Button variant="outlined" onClick={generatePreview} disabled={!previewAssetId || generating}>
            {generating ? <CircularProgress size={20} /> : 'Generera'}
          </Button>
        </Box>
        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
          Alla ändringar i mallen triggar en ny förhandsgranskning så snart en testbild är vald.
        </Typography>

        {manualCoverEnabled && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '12px', borderColor: '#e0e0e0' }}>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
              Justera utsnitt i mallen
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: '#666', mb: 1.5 }}>
              Dra bilden för att flytta fokus i mallen. Zoom och position sparas i mallen och används i den faktiska renderingen.
            </Typography>

            {originalImageSrc ? (
              <>
                <Box
                  ref={cropEditorRef}
                  onMouseDown={handleCropDragStart}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: `${sizing.width} / ${sizing.height}`,
                    overflow: 'hidden',
                    borderRadius: '10px',
                    border: '1px solid #d0d7e2',
                    background: 'repeating-conic-gradient(#f1f1f1 0% 25%, white 0% 50%) 50% / 20px 20px',
                    cursor: cropDragState ? 'grabbing' : 'grab',
                    mb: 2,
                  }}
                >
                  <img
                    src={originalImageSrc}
                    alt="Original för utsnittsjustering"
                    draggable={false}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: `${sizing.focusX}% ${sizing.focusY}%`,
                      transform: `scale(${sizing.zoom})`,
                      transformOrigin: `${sizing.focusX}% ${sizing.focusY}%`,
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      border: '2px solid rgba(255,255,255,0.9)',
                      boxShadow: 'inset 0 0 0 1px rgba(7,47,83,0.18)',
                      pointerEvents: 'none',
                    }}
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption">Zoom i mallen</Typography>
                      <Typography variant="caption">{Math.round(sizing.zoom * 100)}%</Typography>
                    </Box>
                    <Slider
                      value={Math.round(sizing.zoom * 100)}
                      min={100}
                      max={300}
                      step={5}
                      onChange={(_, value) => updateSizing({ zoom: Number(value) / 100 })}
                    />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption">Horisontell position</Typography>
                      <Typography variant="caption">{Math.round(sizing.focusX)}%</Typography>
                    </Box>
                    <Slider
                      value={sizing.focusX}
                      min={0}
                      max={100}
                      step={1}
                      onChange={(_, value) => updateSizing({ focusX: Number(value) })}
                    />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption">Vertikal position</Typography>
                      <Typography variant="caption">{Math.round(sizing.focusY)}%</Typography>
                    </Box>
                    <Slider
                      value={sizing.focusY}
                      min={0}
                      max={100}
                      step={1}
                      onChange={(_, value) => updateSizing({ focusY: Number(value) })}
                    />
                  </Box>
                </Box>
              </>
            ) : (
              <Alert severity="info">Välj en testbild för att kunna flytta och zooma bilden i mallen.</Alert>
            )}
          </Paper>
        )}

        {/* Tabs for Preview/Command */}
        <Box sx={{ mt: 2 }}>
          <Tabs value={previewTab} onChange={(e, v) => setPreviewTab(v)}>
            <Tab label="Förhandsgranskning" />
            <Tab label="Kommando" disabled={!renderCommand} />
          </Tabs>
        </Box>

        {/* Preview Content */}
        <Box sx={{ mt: 2, p: 2, background:'#fafafa', borderRadius: 1, minHeight: 400 }}>
          {previewError && !generating && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {previewError}
            </Alert>
          )}
          {generating && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={48} />
              <Typography variant="body2" sx={{ mt: 2 }}>Genererar förhandsgranskning...</Typography>
            </Box>
          )}
          
          {previewTab === 0 && previewUrl && !generating ? (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: showOriginalCompare && originalImageSrc && !isMobile ? '1fr 1fr' : '1fr',
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: '#666' }}>
                    Genererad bild
                  </Typography>
                  <Box sx={{ p: '30px', background: 'repeating-conic-gradient(#eee 0% 25%, white 0% 50%) 50% / 20px 20px', borderRadius: 1 }}>
                    <img
                      src={previewUrl}
                      alt="Genererad preview"
                      style={{
                        width: `${previewScale}%`,
                        maxWidth: '100%',
                        display: 'block',
                        margin: '0 auto',
                      }}
                    />
                  </Box>
                </Box>
                {showOriginalCompare && originalImageSrc && (
                  <Box>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: '#666' }}>
                      Originalbild
                    </Typography>
                    <Box sx={{ p: '30px', background: 'repeating-conic-gradient(#eee 0% 25%, white 0% 50%) 50% / 20px 20px', borderRadius: 1 }}>
                      <img
                        src={originalImageSrc}
                        alt="Original"
                        style={{
                          width: `${previewScale}%`,
                          maxWidth: '100%',
                          display: 'block',
                          margin: '0 auto',
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
              
              {originalAssetInfo && (
                <Box sx={{ mb: 2, p: 1.5, background: '#fff', borderRadius: 1, border: '1px solid #ddd' }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>📄 Originalinfo:</Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    {originalAssetInfo.width}x{originalAssetInfo.height} px
                    {originalAssetInfo.dpi ? ` @ ${originalAssetInfo.dpi} DPI` : ''}
                    {' • '}{((originalAssetInfo.fileSize || 0) / 1024).toFixed(1)} KB
                    {' • '}{originalAssetInfo.colorSpace || originalAssetInfo.fileType || '—'}
                    {originalAssetInfo.hasClippingPath ? ' • ✂️ Har urklippsbana' : ''}
                  </Typography>
                </Box>
              )}
              
              {previewBytes !== null && (
                <Box sx={{ mb: 2, p: 1.5, background: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>📊 Bearbetad:</Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    {(previewOutputInfo.width && previewOutputInfo.height)
                      ? `${previewOutputInfo.width}x${previewOutputInfo.height} px`
                      : describeSizing(form.sizing)
                    } • {(previewBytes/1024).toFixed(1)} KB • {form.output.format.toUpperCase()}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>
                    {describeSizing(form.sizing)}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
                <Button variant="contained" onClick={handleDownloadPreview} fullWidth>
                  Ladda ned
                </Button>
                <Button 
                  variant="outlined" 
                  color="success"
                  onClick={async () => {
                    if (!previewAssetId) return;
                    try {
                      // Generate the processed image
                      const resp = await axiosInstance.post('/api/download-templates/render/preview', 
                        { inlineTemplate: serializeTemplateForApi(form), assetId: previewAssetId }, 
                        { responseType: 'arraybuffer' }
                      );
                      
                      // Get filename from headers
                      const disposition: string = resp.headers['content-disposition'] || '';
                      const match = disposition.match(/filename="?([^";]+)"?/i);
                      const filename = match ? decodeURIComponent(match[1]) : `processed-${Date.now()}.${form.output.format}`;
                      
                      // Determine correct MIME type based on format
                      const mimeTypeMap: Record<string, string> = {
                        'jpg': 'image/jpeg',
                        'jpeg': 'image/jpeg',
                        'png': 'image/png',
                        'webp': 'image/webp',
                        'tiff': 'image/tiff',
                        'pdf': 'application/pdf'
                      };
                      const mimeType = mimeTypeMap[form.output.format.toLowerCase()] || 'image/jpeg';
                      
                      // Create file blob with correct MIME type
                      const blob = new Blob([resp.data], { type: mimeType });
                      const file = new File([blob], filename, { type: mimeType });
                      
                      // Upload as new version to the same asset
                      const formData = new FormData();
                      formData.append('file', file);
                      formData.append('replaceAssetId', previewAssetId); // Tell backend to replace this asset
                      
                      const uploadResp = await axiosInstance.post('/api/assets', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      const updatedAsset = uploadResp.data;
                      const updatedAssetId = String(updatedAsset?._id || previewAssetId);
                      const updatedCategoryIds = Array.isArray(updatedAsset?.categories)
                        ? updatedAsset.categories
                            .map((categoryId: any) => String(categoryId?._id || categoryId || ''))
                            .filter(Boolean)
                        : [];
                      window.setTimeout(() => {
                        try {
                          window.dispatchEvent(new CustomEvent('dam:upload-batch-completed', {
                            detail: {
                              assetIds: updatedAssetId ? [updatedAssetId] : [],
                              categoryIds: updatedCategoryIds,
                            },
                          }));
                        } catch {}
                      }, 100);
                      
                      setToast({ open: true, message: 'Bild sparad som ny version!', severity: 'success' });
                      
                      // Refresh preview to show new version
                      setTimeout(() => generatePreview(), 500);
                    } catch (e: any) {
                      setToast({ open: true, message: `Fel: ${e?.response?.data?.message || e.message}`, severity: 'error' });
                    }
                  }}
                  fullWidth
                >
                  Spara som ny version
                </Button>
              </Box>
            </>
          ) : previewTab === 1 && renderCommand ? (
            <Paper variant="outlined" sx={{ p: 2, background: '#fff', fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', overflow: 'auto' }}>
              {renderCommand || '—'}
            </Paper>
          ) : !previewUrl && !generating ? (
            <Box sx={{ textAlign: 'center', py: 8, color: '#999' }}>
              <ImageIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
              <Typography variant="body2">Ingen förhandsgranskning genererad ännu.</Typography>
              <Typography variant="caption">Sök efter en bild och klicka på "Generera"</Typography>
            </Box>
          ) : null}
        </Box>
      </Paper>

      {/* Toast Notifications */}
      <Snackbar 
        open={toast.open} 
        autoHideDuration={4000} 
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast({ ...toast, open: false })}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DownloadTemplatesPage;
